import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Alert } from "@mui/material";
import { chatApi } from "../../api/chat.js";
import { useChat } from "../../chat/useChat.js";
import { ChatWindow } from "../../chat/ChatWindow.js";
import type { PublicBotConfig, ChatWidgetTheme } from "@chatfab/shared";

const DEFAULT_THEME: ChatWidgetTheme = {
  primary_color:    "#5C6BC0",
  background_color: "#FFFFFF",
  font_family:      "Inter",
  border_radius:    10,
};

function EmbedChat({ bot, sessionId, theme }: { bot: PublicBotConfig; sessionId: string; theme: ChatWidgetTheme }) {
  const { messages, status, capMessage, sendMessage, sendSuggestedTrigger } = useChat({
    sessionId,
    botId: bot.bot_id,
  });

  // Tell parent window our scroll height for dynamic iframe resize
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      window.parent?.postMessage({ type: "chatfab:resize", height: document.body.scrollHeight }, "*");
    });
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <ChatWindow
        messages={messages}
        status={status}
        capMessage={capMessage}
        onSend={sendMessage}
        botName={bot.name}
        welcomeMessage={bot.embed_config.welcome_message}
        theme={theme}
        showBranding={bot.embed_config.show_branding}
        suggestedTriggers={bot.suggested_triggers}
        onSuggestedTrigger={sendSuggestedTrigger}
      />
    </Box>
  );
}

export function EmbedPage() {
  const { slug }          = useParams<{ slug: string }>();
  const [params]          = useSearchParams();
  const [bot, setBot]     = useState<PublicBotConfig | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [theme, setTheme] = useState<ChatWidgetTheme>(DEFAULT_THEME);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    chatApi.resolveBot(slug)
      .then(async b => {
        setBot(b);
        // Theme from query params (widget passes serialized theme)
        const tParam = params.get("theme");
        if (tParam) {
          try { setTheme({ ...DEFAULT_THEME, ...JSON.parse(atob(tParam)) }); } catch { /* ignore */ }
        }
        const { session_id } = await chatApi.createEmbedSession(slug, document.referrer);
        setSessionId(session_id);
      })
      .catch(err => {
        const msg = err instanceof Error ? err.message : "Unavailable";
        setError(msg);
      });
  }, [slug, params]);

  if (error) return (
    <Box sx={{ p: 2 }}>
      <Alert severity="error" sx={{ fontSize: 12 }}>{error}</Alert>
    </Box>
  );

  if (!bot || !sessionId) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
      <CircularProgress size={28} />
    </Box>
  );

  return <EmbedChat bot={bot} sessionId={sessionId} theme={theme} />;
}
